/**
 * Content Formatter Utility
 *
 * Shared formatting for regulatory content with support for:
 * - Markdown (bold, headers, lists)
 * - LaTeX formulas (via KaTeX)
 * - XML tags (regulatory documents)
 * - Tables
 * - Numbered points
 */

import React from 'react';
import katex from 'katex';
import { Image as ImageIcon } from 'lucide-react';

// Theme configuration
export interface FormatterTheme {
    text: string;           // Main text color
    textMuted: string;      // Secondary text color
    heading: string;        // Header text color
    point: string;          // Point marker color (a), (b), etc.
    subPoint: string;       // Sub-point marker color
    bold: string;           // Bold text color
    tableBorder: string;    // Table border color
    tableHeader: string;    // Table header background
    codeBackground: string; // Code/formula fallback background
    codeText: string;       // Code/formula fallback text
}

export const lightTheme: FormatterTheme = {
    text: 'text-fg-secondary',
    textMuted: 'text-fg-tertiary',
    heading: 'text-fg',
    point: 'text-accent',
    subPoint: 'text-fg-tertiary',
    bold: 'text-fg',
    tableBorder: 'border-border-default',
    tableHeader: 'bg-bg-warm',
    codeBackground: 'bg-bg-warm',
    codeText: 'text-fg-secondary',
};

export const darkTheme: FormatterTheme = {
    text: 'text-fg-secondary',
    textMuted: 'text-fg-tertiary',
    heading: 'text-link',
    point: 'text-link',
    subPoint: 'text-fg-tertiary',
    bold: 'text-fg',
    tableBorder: 'border-border-default',
    tableHeader: 'bg-accent/50',
    codeBackground: 'bg-accent/50',
    codeText: 'text-warning',
};

// Decode HTML entities
function decodeHtmlEntities(text: string): string {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
}

// Convert unicode math symbols to LaTeX
function unicodeToLatex(text: string): string {
    const replacements: [RegExp | string, string][] = [
        // Greek letters
        ['δ', '\\delta'], ['Δ', '\\Delta'], ['σ', '\\sigma'], ['Σ', '\\Sigma'],
        ['λ', '\\lambda'], ['Λ', '\\Lambda'], ['α', '\\alpha'], ['β', '\\beta'],
        ['γ', '\\gamma'], ['Γ', '\\Gamma'], ['ε', '\\varepsilon'], ['η', '\\eta'],
        ['θ', '\\theta'], ['Θ', '\\Theta'], ['μ', '\\mu'], ['π', '\\pi'],
        ['Π', '\\Pi'], ['ρ', '\\rho'], ['τ', '\\tau'], ['φ', '\\phi'],
        ['Φ', '\\Phi'], ['ω', '\\omega'], ['Ω', '\\Omega'],
        // Operators and symbols
        ['⋅', '\\cdot '], ['·', '\\cdot '], ['×', '\\times '], ['÷', '\\div '],
        ['±', '\\pm '], ['∓', '\\mp '], ['√', '\\sqrt'], ['∑', '\\sum'],
        ['∏', '\\prod'], ['∫', '\\int'], ['∂', '\\partial'], ['∞', '\\infty'],
        ['≤', '\\leq '], ['≥', '\\geq '], ['≠', '\\neq '], ['≈', '\\approx '],
        ['∈', '\\in '], ['∉', '\\notin '], ['⊂', '\\subset '], ['⊃', '\\supset '],
        ['∪', '\\cup '], ['∩', '\\cap '], ['∧', '\\land '], ['∨', '\\lor '],
        ['¬', '\\neg '], ['⇒', '\\Rightarrow '], ['⇐', '\\Leftarrow '], ['⇔', '\\Leftrightarrow '],
        ['²', '^2'], ['³', '^3'], ['⁴', '^4'], ['₀', '_0'], ['₁', '_1'], ['₂', '_2'],
    ];

    let result = text;
    for (const [from, to] of replacements) {
        if (from instanceof RegExp) {
            result = result.replace(from, to);
        } else {
            result = result.split(from).join(to);
        }
    }
    return result;
}

// Check if text contains mathematical operators
function isMathematicalContent(text: string): boolean {
    if (/[a-z]{4,}/i.test(text)) return false;
    return /[⋅·×÷±∓√∑∏∫∂∞≤≥≠≈∈∉⊂⊃∪∩∧∨¬⇒⇐⇔²³⁴₀₁₂]/.test(text);
}

// Render LaTeX formula using KaTeX
function renderLatex(latex: string, displayMode: boolean, key: string | number, theme: FormatterTheme): React.ReactNode {
    try {
        const hasMultilineConstruct = /\\begin\{(cases|array|matrix|pmatrix|bmatrix|align)/i.test(latex);
        const actualDisplayMode = displayMode && hasMultilineConstruct;

        const html = katex.renderToString(latex, {
            displayMode: actualDisplayMode,
            throwOnError: false,
            strict: false,
            trust: false,
            macros: { "\\mathit": "\\textit" }
        });

        const textColor = theme === darkTheme ? '#f1f5f9' : '#334155';
        return (
            <span
                key={key}
                style={{ color: textColor }}
                dangerouslySetInnerHTML={{ __html: html }}
            />
        );
    } catch (e) {
        return (
            <code key={key} className={`${theme.codeText} font-mono ${theme.codeBackground} px-1 rounded text-sm`}>
                {latex}
            </code>
        );
    }
}

// Render inline formatting (bold, LaTeX, etc.)
function renderInlineFormatting(
    text: string,
    theme: FormatterTheme,
    displayMathBlocks: string[] = []
): React.ReactNode {
    // eslint-disable-next-line no-useless-escape
    const latexPattern = /(\$\$[\s\S]+?\$\$|\$[^$]+\$|\\\[[\s\S]+?\\\]|\\\([\s\S]+?\\\)|__DISPLAY_MATH_\d+__)/g;
    const parts = text.split(latexPattern);

    return parts.map((part, i) => {
        // Check for display math placeholder
        const placeholderMatch = part.match(/^__DISPLAY_MATH_(\d+)__$/);
        if (placeholderMatch) {
            const mathIndex = parseInt(placeholderMatch[1], 10);
            const mathContent = displayMathBlocks[mathIndex];
            if (mathContent) {
                return renderLatex(mathContent, true, `inline-display-${i}`, theme);
            }
            return null;
        }

        // LaTeX with explicit delimiters
        if (part.startsWith('$$') && part.endsWith('$$')) {
            return renderLatex(part.slice(2, -2).trim(), true, i, theme);
        }
        if (part.startsWith('$') && part.endsWith('$') && part.length > 2) {
            return renderLatex(part.slice(1, -1), false, i, theme);
        }
        if (part.startsWith('\\[') && part.endsWith('\\]')) {
            return renderLatex(part.slice(2, -2).trim(), true, i, theme);
        }
        if (part.startsWith('\\(') && part.endsWith('\\)')) {
            return renderLatex(part.slice(2, -2), false, i, theme);
        }

        // Unicode math symbols
        if (isMathematicalContent(part)) {
            const converted = unicodeToLatex(part);
            return renderLatex(converted, false, i, theme);
        }

        // Handle bold within non-LaTeX parts
        const boldParts = part.split(/(\*\*[^*]+\*\*)/g);
        return boldParts.map((boldPart, j) => {
            if (boldPart.startsWith('**') && boldPart.endsWith('**')) {
                return <strong key={`${i}-${j}`} className={`font-semibold ${theme.bold}`}>{boldPart.slice(2, -2)}</strong>;
            }
            return boldPart;
        });
    });
}

// Render markdown table
function renderTable(tableLines: string[], key: number, theme: FormatterTheme, displayMathBlocks: string[] = []): React.ReactNode {
    const rows = tableLines
        .filter(line => !line.match(/^\|\s*[-:]+/))
        .map(line =>
            line.split('|')
                .slice(1, -1)
                .map(cell => cell.trim())
                .map(cell => cell.replace(/\[IMAGE\]/g, '').trim())
        );

    if (rows.length === 0) return null;
    const nonEmptyRows = rows.filter(row => row.some(cell => cell.length > 0));
    if (nonEmptyRows.length === 0) return null;

    return (
        <div key={key} className="my-3 overflow-x-auto">
            <table className="w-full text-sm border-collapse">
                <thead>
                    <tr className={`border-b ${theme.tableBorder}`}>
                        {nonEmptyRows[0]?.map((cell, i) => (
                            <th key={i} className={`px-3 py-2 text-left ${theme.textMuted} font-medium ${theme.tableHeader}`}>
                                {renderInlineFormatting(cell, theme, displayMathBlocks)}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {nonEmptyRows.slice(1).map((row, i) => (
                        <tr key={i} className={`border-b ${theme.tableBorder}/50`}>
                            {row.map((cell, j) => (
                                <td key={j} className={`px-3 py-2 ${theme.text}`}>
                                    {renderInlineFormatting(cell, theme, displayMathBlocks)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// Check for fragmented formula lines
function isFragmentedFormulaLine(line: string): boolean {
    const trimmed = line.trim();
    if (trimmed.length === 0) return false;
    if (trimmed.length <= 3 && /^[=+\-×÷⋅·\(\)\[\]{}αβγδεζηθικλμνξπρστυφχψω∑∏∫∂∞≤≥≠≈∈∉⊂⊃∪∩∧∨¬⇒⇔\d]+$/i.test(trimmed)) {
        return true;
    }
    return false;
}

/**
 * Parse XML article content and extract structured data
 */
export function parseXmlArticle(content: string): {
    title?: string;
    articleNum?: string;
    hierarchy?: string;
    mainContent?: string;
    doc?: string;
} {
    const result: { title?: string; articleNum?: string; hierarchy?: string; mainContent?: string; doc?: string } = {};

    // Helper to decode HTML entities
    const decode = (str: string): string => {
        return str
            .replace(/&gt;/g, '>')
            .replace(/&lt;/g, '<')
            .replace(/&amp;/g, '&')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/&nbsp;/g, ' ');
    };

    // Extract doc attribute from <article> tag
    const docMatch = content.match(/<article[^>]*doc=["']([^"']+)["']/i);
    if (docMatch) {
        result.doc = decode(docMatch[1]);
    }

    // Extract article number from num or id attribute
    const numMatch = content.match(/<article[^>]*(?:num|id)=["']([^"']+)["']/i);
    if (numMatch) {
        result.articleNum = decode(numMatch[1]);
    }

    // Extract title
    const titleMatch = content.match(/<title>([^<]+)<\/title>/i);
    if (titleMatch) {
        result.title = decode(titleMatch[1].trim());
    }

    // Extract hierarchy
    const hierarchyMatch = content.match(/<hierarchy>([^<]+)<\/hierarchy>/i);
    if (hierarchyMatch) {
        result.hierarchy = decode(hierarchyMatch[1].trim());
    }

    // Extract main content (between <content> tags)
    const contentMatch = content.match(/<content>([\s\S]*?)<\/content>/i);
    if (contentMatch) {
        result.mainContent = contentMatch[1].trim();
    }

    return result;
}

/**
 * Format a hierarchy string into an indented tree display.
 * Input:  "Part Two > Title I > Chapter 4"
 * Output: Indented JSX with each level nested deeper, last level bolded.
 */
export function formatHierarchyTree(hierarchy: string): React.ReactNode {
    if (!hierarchy) return null;

    const parts = hierarchy
        .replace(/&gt;/g, '>')
        .replace(/&lt;/g, '<')
        .replace(/&amp;/g, '&')
        .split('>')
        .map(p => p.trim())
        .filter(Boolean);

    if (parts.length === 0) return null;

    return (
        <div className="space-y-0.5">
            {parts.map((part, i) => (
                <div
                    key={i}
                    style={{ paddingLeft: `${i * 16}px` }}
                    className="text-xs text-fg-secondary flex items-center gap-1"
                >
                    {i > 0 && <span className="text-fg-secondary">&#8250;</span>}
                    <span className={i === parts.length - 1 ? 'font-semibold text-fg' : ''}>
                        {part}
                    </span>
                </div>
            ))}
        </div>
    );
}

/**
 * Strip raw XML wrapper tags that shouldn't be displayed to the user.
 * Removes <search_results>, <result>, <additional_matches>, etc.
 * while preserving the text content inside.
 */
export function cleanSourceText(text: string): string {
    if (!text) return '';

    let cleaned = text
        // Remove search_results wrapper and its attributes
        .replace(/<\/?search_results[^>]*>/gi, '')
        // Remove result tags and their attributes (rank, score, doc, etc.)
        .replace(/<\/?result[^>]*>/gi, '')
        // Remove additional_matches wrapper
        .replace(/<\/?additional_matches[^>]*>/gi, '')
        // Remove search_result tags
        .replace(/<\/?search_result[^>]*>/gi, '')
        // Remove element tags (used inside results)
        .replace(/<\/?element[^>]*>/gi, '')
        // Remove PDF section XML wrappers (context from get_document_section)
        .replace(/<\/?section[^>]*>/gi, '')
        .replace(/<\/?chunks[^>]*>/gi, '')
        .replace(/<chunk[^>]*>/gi, '')
        .replace(/<\/chunk>/gi, '')
        .replace(/<\/?title>/gi, '')
        .replace(/<\/?hierarchy>/gi, '')
        .replace(/<\/?page_range>/gi, '')
        // Remove concept/reference XML tags
        .replace(/<\/?concepts[^>]*>/gi, '')
        .replace(/<\/?concept[^>]*>/gi, '')
        .replace(/<\/?references[^>]*>/gi, '')
        .replace(/<\/?cited_by[^>]*>/gi, '')
        .replace(/<\/?ref[^>]*>/gi, '')
        .replace(/<\/?linked_qa[^>]*>/gi, '')
        .replace(/<\/?qa[^>]*>/gi, '')
        .replace(/<\/?metadata[^>]*>/gi, '')
        // Clean up excess whitespace from tag removal
        .replace(/\n{3,}/g, '\n\n')
        .trim();

    return cleaned;
}

/**
 * Pick the text to show as the agent's quoted reference — in full, never
 * character-truncated.
 *
 * Historical write paths stored source_snippet as a [:N] cut of the full
 * source_text, sometimes behind a "[ref] (doc)" header line the full text
 * does not carry (commit_research_block's flattened content). Those
 * FINDING_CREATED events are immutable — replay keeps the capped snippet
 * forever — so the cut has to be detected at render: a snippet whose body
 * (header stripped) is a strict prefix of source_text is a stored cut →
 * show the full text. Anything else is a curated excerpt complete by
 * design (a PDF matched paragraph, multi-result flattened content) →
 * show it whole.
 */
export function resolveAgentReferenceText(
    sourceText?: string | null,
    sourceSnippet?: string | null,
): string {
    const snippet = sourceSnippet || '';
    const text = sourceText || '';
    if (!snippet) return text;
    if (!text) return snippet;
    // Header line = "[ref] (doc)". Doc names nest parens ("(Regulation (EU)
    // 2017/2402)"), so strip the whole line rather than matching the parens.
    const body = snippet.replace(/^\[[^\]\n]*\][^\n]*\n/, '');
    const snippetIsTruncatedCut =
        !!body && text.length > body.length && text.startsWith(body);
    return snippetIsTruncatedCut ? text : snippet;
}

/**
 * Check if content is XML article format
 */
function isXmlArticle(content: string): boolean {
    return /<(?:article|qa)[^>]*>/.test(content) && /<content>/.test(content);
}

/** Render canonical source XML through an explicit element allowlist. */
function formatCanonicalContent(content: string, theme: FormatterTheme): React.ReactNode | undefined {
    if (!/<(?:block|table|formula|image)\b[^>]*(?:id=|format=|status=|encoding=)/i.test(content)) return undefined;
    const doc = new DOMParser().parseFromString(`<source>${content}</source>`, 'application/xml');
    if (doc.querySelector('parsererror')) return undefined;
    const span = (element: Element, name: string): number | undefined => {
        const value = element.getAttribute(name);
        if (!value || !/^\d+$/.test(value)) return undefined;
        const parsed = Number(value);
        return parsed >= 1 && parsed <= 1000 ? parsed : undefined;
    };
    const renderNode = (node: Node, key: string): React.ReactNode => {
        if (node.nodeType === Node.TEXT_NODE) {
            if (['table', 'tr'].includes(node.parentNode?.nodeName || '') && !node.textContent?.trim()) return null;
            const text = (node.textContent || '').replace(/\[IMAGE(?:_\d+)?\]/g, 'Source image unavailable in this saved excerpt; retrieve the source again.');
            return <React.Fragment key={key}>{renderInlineFormatting(text, theme)}</React.Fragment>;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) return null;
        const element = node as Element;
        const caption = Array.from(element.children).find(child => child.tagName === 'caption')?.textContent;
        const children = Array.from(element.childNodes).map((child, i) => renderNode(child, `${key}-${i}`));
        switch (element.tagName) {
            case 'source': case 'content': return <React.Fragment key={key}>{children}</React.Fragment>;
            case 'block': return <div key={key} data-content-id={element.getAttribute('id')} className={`${theme.text} my-1 whitespace-pre-wrap`}>{children}</div>;
            case 'table': return (
                <div key={key} className="overflow-x-auto my-3">
                    <table data-source-table-id={element.getAttribute('id')} className={`border-collapse border ${theme.tableBorder} text-sm`}>
                        {caption && <caption className="font-medium mb-1">{caption}</caption>}
                        <tbody>{children}</tbody>
                    </table>
                </div>
            );
            case 'caption': return null; // The table renders its caption outside tbody.
            case 'tr': return <tr key={key}>{children}</tr>;
            case 'td': return <td key={key} rowSpan={span(element, 'rowspan')} colSpan={span(element, 'colspan')} className={`border ${theme.tableBorder} px-3 py-2 align-top`}>{children}</td>;
            case 'formula': return <React.Fragment key={key}>{renderInlineFormatting(element.textContent || '', theme)}</React.Fragment>;
            case 'image': {
                const mime = element.getAttribute('mime_type') || '';
                const payload = element.textContent || '';
                if (element.getAttribute('encoding') === 'base64' && /^image\/(png|jpeg|gif|webp)$/.test(mime) && /^[A-Za-z0-9+/=\s]+$/.test(payload)) {
                    return <img key={key} src={`data:${mime};base64,${payload}`} alt="Source document image" className="max-w-full h-auto my-2" />;
                }
                return <span key={key} role="note" className={theme.textMuted}>{payload || 'Source image unavailable; consult the publisher source.'}</span>;
            }
            default: return null;
        }
    };
    return renderNode(doc.documentElement, 'source');
}

/**
 * Main formatting function - renders content with full formatting support
 */
export function formatContent(content: string, theme: FormatterTheme = lightTheme): React.ReactNode {
    if (!content) return null;

    // Check if this is XML article format - extract just the content part
    if (isXmlArticle(content)) {
        const parsed = parseXmlArticle(content);
        if (parsed.mainContent) {
            // Format just the main content, not the XML wrapper
            return formatContentInner(parsed.mainContent, theme);
        }
    }

    return formatContentInner(content, theme);
}

function linesToParagraphs(lines: string[]): string[] {
    const paragraphs: string[] = [];
    let current: string[] = [];

    for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) {
            if (current.length > 0) {
                paragraphs.push(current.join(' '));
                current = [];
            }
            continue;
        }
        current.push(trimmed);
    }

    if (current.length > 0) {
        paragraphs.push(current.join(' '));
    }

    return paragraphs;
}

/**
 * Format EBA Q&A content as explicit metadata, question, and answer sections.
 * This intentionally avoids the generic article-heading parser because Q&A
 * answers often begin with strings like "Article 27 - (4)", which are body
 * text in this context rather than headings.
 */
export function formatQaContent(content: string, theme: FormatterTheme = lightTheme): React.ReactNode {
    if (!content) return null;

    const canonical = formatCanonicalContent(parseXmlArticle(content).mainContent || content, theme);
    if (canonical !== undefined) return canonical;

    const decodedContent = decodeHtmlEntities(content);
    const lines = decodedContent
        .replace(/\t+/g, ' ')
        .replace(/  +/g, ' ')
        .split(/\r?\n/);

    const isQuestionLabel = (line: string) => /^(?:question|q\s*:)$/i.test(line.trim());
    const isAnswerLabel = (line: string) => /^(?:answer|a\s*:)$/i.test(line.trim());

    const questionIndex = lines.findIndex(isQuestionLabel);
    const answerIndex = lines.findIndex(isAnswerLabel);

    if (questionIndex === -1 || answerIndex === -1 || answerIndex < questionIndex) {
        return (
            <div className="space-y-3">
                {linesToParagraphs(lines).map((paragraph, index) => (
                    <p key={index} className={`${theme.text} leading-relaxed text-sm`}>
                        {renderInlineFormatting(paragraph, theme)}
                    </p>
                ))}
            </div>
        );
    }

    const metaLines = lines.slice(0, questionIndex).filter(line => line.trim());
    const questionParagraphs = linesToParagraphs(lines.slice(questionIndex + 1, answerIndex));
    const answerParagraphs = linesToParagraphs(lines.slice(answerIndex + 1));

    return (
        <div className="space-y-5">
            {metaLines.length > 0 && (
                <div className="font-mono text-[12px] text-fg-secondary">
                    {metaLines.join(' · ')}
                </div>
            )}

            <section>
                <h4 className="text-[12px] font-bold uppercase tracking-[0.08em] text-fg mb-2">
                    Question
                </h4>
                <div className="space-y-3">
                    {questionParagraphs.map((paragraph, index) => (
                        <p key={index} className={`${theme.text} leading-relaxed text-sm`}>
                            {renderInlineFormatting(paragraph, theme)}
                        </p>
                    ))}
                </div>
            </section>

            <section>
                <h4 className="text-[12px] font-bold uppercase tracking-[0.08em] text-fg mb-2">
                    Answer
                </h4>
                <div className="space-y-3">
                    {answerParagraphs.map((paragraph, index) => (
                        <p key={index} className={`${theme.text} leading-relaxed text-sm`}>
                            {renderInlineFormatting(paragraph, theme)}
                        </p>
                    ))}
                </div>
            </section>
        </div>
    );
}

/**
 * Inner formatting function - handles the actual content formatting
 */
function formatContentInner(content: string, theme: FormatterTheme): React.ReactNode {
    if (!content) return null;

    const canonical = formatCanonicalContent(content, theme);
    if (canonical !== undefined) return canonical;

    // Decode HTML entities
    const decodedContent = decodeHtmlEntities(content);

    // Clean up content (preserve [IMAGE...][END IMAGE] blocks for rendering)
    let cleanedContent = decodedContent
        .replace(/\[IMAGE\]/g, '')  // Strip bare [IMAGE] markers (no description)
        .replace(/\t+/g, ' ')
        .replace(/  +/g, ' ');

    // Extract display math blocks before splitting by newlines
    const displayMathBlocks: string[] = [];
    let contentWithPlaceholders = cleanedContent;

    contentWithPlaceholders = contentWithPlaceholders.replace(/\$\$([\s\S]*?)\$\$/g, (_, math) => {
        const index = displayMathBlocks.length;
        displayMathBlocks.push(math.trim());
        return `__DISPLAY_MATH_${index}__`;
    });

    contentWithPlaceholders = contentWithPlaceholders.replace(/\$([^$]*\n[^$]*)\$/g, (_, math) => {
        const index = displayMathBlocks.length;
        displayMathBlocks.push(math.trim());
        return `__DISPLAY_MATH_${index}__`;
    });

    const lines = contentWithPlaceholders.split('\n');
    const elements: React.ReactNode[] = [];
    let tableLines: string[] = [];
    let inTable = false;
    let formulaLines: string[] = [];
    let inFormula = false;

    const processLine = (line: string, index: number): React.ReactNode => {
        const trimmed = line.trim();

        // Display math placeholder
        const displayMathMatch = trimmed.match(/^__DISPLAY_MATH_(\d+)__$/);
        if (displayMathMatch) {
            const mathIndex = parseInt(displayMathMatch[1], 10);
            const mathContent = displayMathBlocks[mathIndex];
            if (mathContent) {
                return renderLatex(mathContent, true, `display-math-${index}`, theme);
            }
        }

        // Empty line
        if (!trimmed) {
            return <div key={index} className="h-2" />;
        }

        // Markdown section header: ## 1. or ## Title
        const sectionMatch = trimmed.match(/^##\s*(\d+\.?|[A-Z].*?)\.?\s*$/);
        if (sectionMatch) {
            return (
                <h4 key={index} className={`text-sm font-bold ${theme.heading} mt-4 mb-2 border-b ${theme.tableBorder} pb-1`}>
                    {sectionMatch[1]}
                </h4>
            );
        }

        // Bold paragraph header: **Paragraph 1**
        const paragraphMatch = trimmed.match(/^\*\*Paragraph\s+(\d+)\*\*$/);
        if (paragraphMatch) {
            return (
                <h4 key={index} className={`text-sm font-semibold ${theme.heading} mt-4 mb-2 border-b ${theme.tableBorder} pb-1`}>
                    Paragraph {paragraphMatch[1]}
                </h4>
            );
        }

        // Article header
        const articleMatch = trimmed.match(/^(Article\s+\d+[a-z]?)\s*[-–—]?\s*(.*)$/i);
        if (articleMatch) {
            return (
                <h4 key={index} className={`text-sm font-semibold ${theme.heading} mt-4 mb-2`}>
                    {articleMatch[1]}{articleMatch[2] ? ` - ${articleMatch[2]}` : ''}
                </h4>
            );
        }

        // Bold point marker: **(a)** or **(i)** with optional content
        const boldPointMatch = trimmed.match(/^\*\*\(([a-z]+|[ivxlc]+|\d+)\)\*\*\s*(.*)$/i);
        if (boldPointMatch) {
            return (
                <div key={index} className="flex ml-4 my-1">
                    <span className={`${theme.point} font-bold w-10 flex-shrink-0`}>({boldPointMatch[1]})</span>
                    <span className={theme.text}>{renderInlineFormatting(boldPointMatch[2], theme, displayMathBlocks)}</span>
                </div>
            );
        }

        // Point: (a), (b), (i), (ii), etc.
        const pointMatch = trimmed.match(/^\(([a-z]+|[ivxlc]+|\d+)\)\s*(.*)$/i);
        if (pointMatch) {
            return (
                <div key={index} className="flex ml-4 my-1">
                    <span className={`${theme.point} font-medium w-8 flex-shrink-0`}>({pointMatch[1]})</span>
                    <span className={theme.text}>{renderInlineFormatting(pointMatch[2], theme, displayMathBlocks)}</span>
                </div>
            );
        }

        // Sub-point (indented)
        const subPointMatch = line.match(/^\s{2,}\(([a-z]+|[ivxlc]+|\d+)\)\s*(.*)$/i);
        if (subPointMatch) {
            return (
                <div key={index} className="flex ml-8 my-1">
                    <span className={`${theme.subPoint} font-medium w-8 flex-shrink-0`}>({subPointMatch[1]})</span>
                    <span className={theme.textMuted}>{renderInlineFormatting(subPointMatch[2], theme, displayMathBlocks)}</span>
                </div>
            );
        }

        // Numbered point: 1., 2., etc.
        const numberedMatch = trimmed.match(/^(\d+)\.\s+(.*)$/);
        if (numberedMatch) {
            return (
                <div key={index} className="flex ml-4 my-1">
                    <span className={`${theme.point} font-medium w-8 flex-shrink-0`}>{numberedMatch[1]}.</span>
                    <span className={theme.text}>{renderInlineFormatting(numberedMatch[2], theme, displayMathBlocks)}</span>
                </div>
            );
        }

        // Regular text
        return (
            <p key={index} className={`${theme.text} my-1 leading-relaxed text-sm`}>
                {renderInlineFormatting(trimmed, theme, displayMathBlocks)}
            </p>
        );
    };

    const renderFormulaBlock = (formulaText: string, key: number): React.ReactNode => {
        const converted = unicodeToLatex(formulaText);
        return renderLatex(converted, true, `formula-${key}`, theme);
    };

    // Process lines
    let inImageBlock = false;
    let imageHeader = '';
    let imageDescLines: string[] = [];

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const trimmed = line.trim();

        // Image block detection: [IMAGE from doc, page N]
        const imageStartMatch = trimmed.match(/^\[IMAGE(?:\s+from\s+([^,\]]+))?(?:,\s*page\s+(\d+))?\s*\]$/i);
        if (imageStartMatch) {
            inImageBlock = true;
            const doc = imageStartMatch[1] || '';
            const page = imageStartMatch[2] || '';
            imageHeader = page ? `Figure (page ${page})` : doc ? `Figure — ${doc}` : 'Figure';
            imageDescLines = [];
            continue;
        }
        if (trimmed === '[END IMAGE]') {
            inImageBlock = false;
            const desc = imageDescLines.join(' ').trim();
            if (desc) {
                elements.push(
                    <div key={`img-${elements.length}`} className="my-3 p-3 bg-bg-warm rounded-lg border border-border-default">
                        <div className="flex items-center gap-1.5 text-[10px] font-semibold text-fg-tertiary uppercase tracking-wide mb-1.5">
                            <ImageIcon size={16} strokeWidth={1.5} aria-hidden="true" />
                            <span>{imageHeader}</span>
                        </div>
                        <p className="text-xs text-fg-secondary leading-relaxed">{desc}</p>
                    </div>
                );
            }
            imageHeader = '';
            imageDescLines = [];
            continue;
        }
        if (inImageBlock) {
            imageDescLines.push(trimmed);
            continue;
        }

        // Table header marker: [TABLE on page N, ...] or [END TABLE]
        if (trimmed.match(/^\[TABLE\s+on\s+page\s+\d+/i)) {
            // Extract page number for label, render before table
            const tablePageMatch = trimmed.match(/page\s+(\d+)/i);
            const tableInfo = tablePageMatch ? `Table (page ${tablePageMatch[1]})` : 'Table';
            elements.push(
                <div key={`tbl-hdr-${elements.length}`} className="text-[10px] font-semibold text-fg-tertiary uppercase tracking-wide mt-3 mb-1">
                    {tableInfo}
                </div>
            );
            continue;
        }
        if (trimmed === '[END TABLE]') {
            continue;  // Skip end marker
        }

        // Table detection
        if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
            if (inFormula && formulaLines.length > 0) {
                elements.push(renderFormulaBlock(formulaLines.join(''), elements.length));
                formulaLines = [];
                inFormula = false;
            }
            if (!inTable) {
                inTable = true;
                tableLines = [];
            }
            tableLines.push(trimmed);
            continue;
        }

        // End of table
        if (inTable && tableLines.length > 0) {
            elements.push(renderTable(tableLines, elements.length, theme, displayMathBlocks));
            tableLines = [];
            inTable = false;
        }

        // Fragmented formula detection
        if (isFragmentedFormulaLine(line)) {
            inFormula = true;
            formulaLines.push(trimmed);
            continue;
        }

        // End of fragmented formula
        if (inFormula && formulaLines.length > 0) {
            elements.push(renderFormulaBlock(formulaLines.join(''), elements.length));
            formulaLines = [];
            inFormula = false;
        }

        elements.push(processLine(line, i));
    }

    // Handle trailing table
    if (inTable && tableLines.length > 0) {
        elements.push(renderTable(tableLines, elements.length, theme, displayMathBlocks));
    }

    // Handle trailing formula
    if (inFormula && formulaLines.length > 0) {
        elements.push(renderFormulaBlock(formulaLines.join(''), elements.length));
    }

    return <>{elements}</>;
}
